import { Box, Chip, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { MoneyParts } from '@/components/MoneyText';
import { AddIcon, InfoIcon } from '@/components/icons';
import { useTranslation } from 'react-i18next';
import type { Occurrence } from '@/api/planned';
import { MonthCalendar } from '@/components/MonthCalendar';
import { statusLabel } from '@/components/statusLabel';
import { occurrenceLabelValues } from './paycheckParts';
import { toneChipSx } from '@/theme/toneChipSx';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { toneFor } from '@/theme/statusTones';
import { resolveTokens } from '@/theme/theme';

interface OccurrenceCalendarProps {
  year: number;
  monthIndex: number;
  occurrences: Occurrence[];
  defaultCurrency: string | null;
  currencyBySeries: Record<number, string | null>;
  seriesNames: Record<number, string>;
  subscriber: boolean;
  onMonthChange: (year: number, monthIndex: number) => void;
  onSelect: (occ: Occurrence) => void;
  onAddOnDay: (civil: string) => void;
  /**
   * The month was served from today, later than its first day (TMMAPI-0188 U3: history is the
   * registers): a "From today" caption, so the grid doesn't imply the whole month (TMMAPP-0159 item 26).
   */
  fromToday?: boolean;
}

/**
 * Month grid of occurrence chips (`md`+); chronological list on `xs` (TMMAPP-0009).
 * Shell is the shared `MonthCalendar` (TMMAPP-0014).
 */
export function OccurrenceCalendar({
  year,
  monthIndex,
  occurrences,
  defaultCurrency,
  currencyBySeries,
  seriesNames,
  subscriber,
  onMonthChange,
  onSelect,
  onAddOnDay,
  fromToday = false,
}: OccurrenceCalendarProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());

  function chip(occ: Occurrence) {
    const currency = currencyBySeries[occ.seriesId] ?? defaultCurrency;
    const struck = occ.state === 'CANCELLED';
    const dim = occ.state === 'SUPERSEDED';
    const toneSx = toneChipSx(tokens, toneFor('occurrence', occ.state));
    // Superseded reads as text2 on the tone's fill: the root colour, the label, and the icon.
    // The hover and focus rules `toneChipSx` sets restate the ink, so they take it too.
    const dimInk = {
      color: tokens.text2,
      '& .MuiChip-icon': { color: tokens.text2 },
    };
    const differs =
      occ.differsFromSeries !== null &&
      (occ.differsFromSeries.date || occ.differsFromSeries.amount || occ.differsFromSeries.slices);
    const name = occ.payee ?? seriesNames[occ.seriesId] ?? '';
    const el = (
      <Chip
        key={`${occ.seriesId}-${occ.originalOn}`}
        size="small"
        label={
          <Stack direction="row" component="span" sx={{ alignItems: 'center', gap: 0.5 }}>
            {differs && (
              <Box
                component="span"
                role="img"
                aria-label={t('planned.edited')}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  // The chip's own label ink: `<status>Ink` on a status fill, `text` on the
                  // default chip's `selected` wash — `infoFg` is guaranteed on neither.
                  bgcolor: 'currentColor',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
            )}
            {name} <MoneyParts minor={occ.amountMinor} currency={currency} />
            {` · ${statusLabel(t, 'occurrence', occ.state, occurrenceLabelValues(occ.amountMinor))}`}
          </Stack>
        }
        onClick={() => onSelect(occ)}
        icon={
          occ.pending === true ? (
            <Box
              component="span"
              role="img"
              aria-label={t('planned.pendingFlag')}
              sx={{ display: 'inline-flex' }}
            >
              <InfoIcon />
            </Box>
          ) : undefined
        }
        sx={{
          // The state's tone (TMMAPP-0070); the label says it in words too.
          ...toneSx,
          textDecoration: struck ? 'line-through' : undefined,
          // Superseded is still read: text2 on the chip's surface, not a fade.
          ...(dim
            ? {
                ...dimInk,
                '& .MuiChip-label': { color: tokens.text2 },
                '&.MuiChip-clickable:hover': {
                  ...toneSx['&.MuiChip-clickable:hover'],
                  ...dimInk,
                },
                '&.Mui-focusVisible': { ...toneSx['&.Mui-focusVisible'], ...dimInk },
              }
            : {}),
          maxWidth: '100%',
        }}
      />
    );
    return occ.pending === true ? (
      <Tooltip
        key={`${occ.seriesId}-${occ.originalOn}-tip`}
        describeChild
        title={t('planned.pendingFlag')}
      >
        {el}
      </Tooltip>
    ) : (
      el
    );
  }

  return (
    <>
      {fromToday && (
        <Typography
          variant="caption"
          component="p"
          data-from-today=""
          sx={{ m: 0, mb: 1, color: tokens.textLabel('surface') }}
        >
          {t('forecast.fromToday')}
        </Typography>
      )}
      <MonthCalendar
        month={{ year, monthIndex }}
        entries={occurrences.map((occ) => ({
          date: occ.occursOn,
          key: `${occ.seriesId}-${occ.originalOn}`,
          render: () => chip(occ),
        }))}
        onNavigate={onMonthChange}
        emptyLabel={t('planned.emptyCalendar')}
        {...(subscriber
          ? {
              dayAccessory: (civil: string) => (
                <IconButton
                  size="small"
                  aria-label={t('planned.addOnDay', { date: formatCivilDate(civil, locale) })}
                  onClick={() => onAddOnDay(civil)}
                >
                  <AddIcon />
                </IconButton>
              ),
            }
          : {})}
      />
    </>
  );
}
